import { useCallback, useEffect, useState } from "react";
import { api, apiError, fmt, hhmm } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { ImageField } from "@/components/ImageField";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Dices, Gift, Clock, CalendarDays } from "lucide-react";

const NUM_FIELDS = [
  ["minutes_per_entry", "Minutes per entry"],
  ["interval_days", "Days between draws"],
  ["winners_count", "Winners per draw"],
  ["cooldown_days", "Winner cooldown (days)"],
  ["claim_window_hours", "Claim window (hours)"],
  ["max_entries_per_period", "Max entries per player (0 = no cap)"],
  ["extra_entry_points", "Points per extra entry (0 = off)"],
  ["max_extra_entries", "Max extra entries per player"],
];

const toYmd = (d) =>
  `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;

const WIN_STATUS = {
  pending_claim: "border-amber-500/50 bg-amber-500/10 text-amber-300",
  claimed: "border-primary/50 bg-primary/10 text-primary",
  fulfilled: "border-emerald-500/50 bg-emerald-500/10 text-emerald-300",
  expired: "border-border text-muted-foreground",
  rejected: "border-destructive/50 text-destructive",
};

export const GiveawayAdmin = () => {
  const [d, setD] = useState(null);
  const [cfg, setCfg] = useState(null);
  const [prize, setPrize] = useState({ prize_title: "", prize_description: "", prize_image_url: "" });

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/admin/giveaway");
      setD(data);
      setCfg(data.config);
      setPrize({
        prize_title: data.prize.prize_title || "",
        prize_description: data.prize.prize_description || "",
        prize_image_url: data.prize.prize_image_url || "",
      });
    } catch (e) {
      toast.error(apiError(e));
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const save = async () => {
    try {
      const body = { ...cfg };
      NUM_FIELDS.forEach(([k]) => (body[k] = Number(body[k]) || 0));
      await api.put("/admin/giveaway", body);
      toast.success("Giveaway rules saved");
      load();
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  const saveOverride = async () => {
    try {
      await api.post("/admin/giveaway/prize", prize);
      toast.success("Prize set for this round only");
      load();
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  const clearOverride = async () => {
    try {
      await api.delete("/admin/giveaway/prize");
      toast.success("Back to the default prize");
      load();
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  const draw = async (force) => {
    try {
      const { data } = await api.post(`/admin/giveaway/draw?force=${force ? "true" : "false"}`);
      toast.success(
        data.winners.length
          ? `Winner: ${data.winners.map((w) => w.username).join(", ")}`
          : "Draw ran but nobody had entries"
      );
      load();
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  const setStatus = async (id, status) => {
    try {
      await api.post(`/admin/giveaway/wins/${id}/status`, { status, note: "" });
      toast.success(`Marked ${status.replace("_", " ")}`);
      load();
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  if (!d || !cfg) return null;

  return (
    <div className="space-y-4" data-testid="giveaway-admin">
      <div className="border border-border bg-card p-8">
        <div className="flex flex-wrap items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            <Dices className="h-5 w-5 text-primary" />
            <h3 className="text-lg font-semibold">Round {d.period_key}</h3>
            <Badge variant="secondary" className="rounded-none uppercase" data-testid="giveaway-round-badge">
              {fmt(d.entries_total)} entries · {d.participants} players
            </Badge>
          </div>
          <div className="flex gap-3">
            <Button variant="outline" className="rounded-none" onClick={() => draw(false)} data-testid="run-draw-btn">
              Run draw now
            </Button>
            <Button variant="ghost" className="rounded-none" onClick={() => draw(true)} data-testid="force-draw-btn">
              Force re-draw
            </Button>
          </div>
        </div>
        <div className="mt-4 flex flex-wrap items-center gap-2 text-xs text-muted-foreground">
          <Clock className="h-3.5 w-3.5" />
          opens {new Date(d.opens_at).toLocaleString()} · draws {new Date(d.draws_at).toLocaleString()} ({cfg.timezone})
        </div>

        <div className="mt-6 grid gap-4 md:grid-cols-4">
          {NUM_FIELDS.map(([key, label]) => (
            <div key={key}>
              <Label className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground">{label}</Label>
              <Input
                type="number"
                data-testid={`giveaway-${key}`}
                className="mt-2 rounded-none bg-secondary mono"
                value={cfg[key] ?? 0}
                onChange={(e) => setCfg({ ...cfg, [key]: e.target.value })}
              />
            </div>
          ))}
          <div>
            <Label className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground">Draw time (24h)</Label>
            <Input
              data-testid="giveaway-draw_time"
              className="mt-2 rounded-none bg-secondary mono"
              value={cfg.draw_time}
              onChange={(e) => setCfg({ ...cfg, draw_time: e.target.value })}
            />
          </div>
          <div>
            <Label className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground">Timezone</Label>
            <Input
              data-testid="giveaway-timezone"
              className="mt-2 rounded-none bg-secondary mono"
              value={cfg.timezone}
              onChange={(e) => setCfg({ ...cfg, timezone: e.target.value })}
            />
          </div>
          <div>
            <Label className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground">
              Draw day (calendar)
            </Label>
            <Popover>
              <PopoverTrigger asChild>
                <Button
                  variant="outline"
                  data-testid="giveaway-anchor-date"
                  className="mono mt-2 w-full justify-start rounded-none bg-secondary font-normal"
                >
                  <CalendarDays className="mr-2 h-3.5 w-3.5 text-primary" />
                  {cfg.anchor_date || "pick a day"}
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-auto rounded-none p-0" align="start">
                <Calendar
                  mode="single"
                  selected={cfg.anchor_date ? new Date(`${cfg.anchor_date}T12:00:00`) : undefined}
                  onSelect={(day) => day && setCfg({ ...cfg, anchor_date: toYmd(day) })}
                  initialFocus
                />
                <div className="flex justify-end border-t border-border p-2">
                  <Button
                    size="sm"
                    variant="ghost"
                    className="rounded-none text-xs"
                    data-testid="giveaway-anchor-clear"
                    onClick={() => setCfg({ ...cfg, anchor_date: "" })}
                  >
                    Clear
                  </Button>
                </div>
              </PopoverContent>
            </Popover>
            <p className="mt-1.5 text-[11px] text-muted-foreground">
              The next draw lands on this day at the draw time, then repeats every{" "}
              {Number(cfg.interval_days) || 1} day(s)
            </p>
          </div>
        </div>

        <div className="mt-6 flex flex-wrap gap-8 border-t border-border pt-6">
          <div className="flex items-center gap-3">
            <Switch
              data-testid="giveaway-enabled"
              checked={!!cfg.enabled}
              onCheckedChange={(v) => setCfg({ ...cfg, enabled: v })}
            />
            <span className="text-sm">Giveaway running</span>
          </div>
          <div className="flex items-center gap-3">
            <Switch
              data-testid="giveaway-redraw"
              checked={!!cfg.redraw_unclaimed}
              onCheckedChange={(v) => setCfg({ ...cfg, redraw_unclaimed: v })}
            />
            <span className="text-sm">Re-draw prizes nobody claims in time</span>
          </div>
          <Button className="ml-auto rounded-none" onClick={save} data-testid="save-giveaway-btn">
            Save rules
          </Button>
        </div>
      </div>

      <div className="border border-border bg-card p-8" data-testid="prize-editor">
        <div className="flex items-center gap-3">
          <Gift className="h-5 w-5 text-primary" />
          <h3 className="text-lg font-semibold">Prize</h3>
          {d.prize_is_override && (
            <Badge className="rounded-none uppercase" data-testid="override-badge">
              this round only
            </Badge>
          )}
        </div>
        <p className="mt-3 text-sm text-muted-foreground">
          Saving here changes only the {d.period_key} round. The default prize used by every other round
          lives in the rules above ("{cfg.prize_title}").
        </p>
        <div className="mt-5 grid gap-3 md:grid-cols-2">
          <div>
            <Label className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground">Prize title</Label>
            <Input
              data-testid="prize-title"
              className="mt-2 rounded-none bg-secondary"
              value={prize.prize_title}
              onChange={(e) => setPrize({ ...prize, prize_title: e.target.value })}
            />
          </div>
          <div className="md:col-span-2">
            <Label className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground">Prize image</Label>
            <ImageField
              testid="prize-image-field"
              value={prize.prize_image_url}
              onChange={(v) => setPrize({ ...prize, prize_image_url: v })}
            />
          </div>
          <div className="md:col-span-2">
            <Label className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground">Description</Label>
            <Textarea
              data-testid="prize-description"
              className="mt-2 rounded-none bg-secondary"
              value={prize.prize_description}
              onChange={(e) => setPrize({ ...prize, prize_description: e.target.value })}
            />
          </div>
        </div>
        <div className="mt-4 flex gap-3">
          <Button className="rounded-none" onClick={saveOverride} data-testid="save-prize-btn">
            Set this round's prize
          </Button>
          <Button variant="ghost" className="rounded-none" onClick={clearOverride} data-testid="clear-prize-btn">
            Use default
          </Button>
          <Button
            variant="outline"
            className="rounded-none"
            onClick={() => {
              setCfg({ ...cfg, prize_title: prize.prize_title, prize_description: prize.prize_description, prize_image_url: prize.prize_image_url });
              toast.info("Loaded into the rules — hit Save rules to make it the default");
            }}
            data-testid="make-default-prize-btn"
          >
            Make it the default
          </Button>
        </div>
      </div>

      <div className="grid gap-4 lg:grid-cols-2">
        <div className="border border-border bg-card p-8" data-testid="entry-leaders-admin">
          <h3 className="text-lg font-semibold">Entries this round</h3>
          <div className="mt-4 max-h-80 overflow-auto">
            {d.leaders.length === 0 && <p className="text-sm text-muted-foreground">No entries yet.</p>}
            {d.leaders.map((l) => (
              <div key={l.user_id} className="flex items-center justify-between border-b border-border py-2 text-sm last:border-b-0">
                <span>{l.username}</span>
                <span className="mono text-xs text-muted-foreground">
                  {hhmm(l.minutes)}
                  {l.purchased ? ` +${l.purchased} bought` : ""} ·{" "}
                  <span className="text-primary">{fmt(l.entries)}</span>
                </span>
              </div>
            ))}
          </div>
        </div>

        <div className="border border-border bg-card p-8" data-testid="wins-admin">
          <h3 className="text-lg font-semibold">Winners & claims</h3>
          <div className="mt-4 max-h-80 space-y-3 overflow-auto">
            {d.wins.length === 0 && <p className="text-sm text-muted-foreground">No draws have run yet.</p>}
            {d.wins.map((w) => (
              <div key={w.id} className="border border-border p-4 text-sm" data-testid={`admin-win-${w.id}`}>
                <div className="flex flex-wrap items-center justify-between gap-3">
                  <div>
                    <span className="font-semibold">{w.username}</span>
                    <span className="text-muted-foreground"> · {w.prize_title} · {w.period_key}</span>
                  </div>
                  <span className={`border px-2 py-0.5 text-[10px] uppercase tracking-[0.15em] ${WIN_STATUS[w.status]}`}>
                    {w.status.replace("_", " ")}
                  </span>
                </div>
                {w.claim ? (
                  <div className="mono mt-3 space-y-0.5 text-xs text-muted-foreground">
                    <div>{w.claim.character_name} · discord {w.claim.discord_tag || "—"}</div>
                    <div>{w.claim.full_name}, age {w.claim.age}</div>
                    <div>
                      {w.claim.address_line}, {w.claim.city} {w.claim.state} {w.claim.postal_code}{" "}
                      {w.claim.country}
                    </div>
                    {w.claim.note && <div>note: {w.claim.note}</div>}
                  </div>
                ) : (
                  <div className="mt-2 text-xs text-muted-foreground">
                    Waiting on their claim form · deadline {new Date(w.claim_deadline).toLocaleString()}
                  </div>
                )}
                <div className="mt-3 flex gap-2">
                  <Button size="sm" className="rounded-none" onClick={() => setStatus(w.id, "fulfilled")} data-testid={`fulfil-win-${w.id}`}>
                    Mark delivered
                  </Button>
                  <Button size="sm" variant="ghost" className="rounded-none" onClick={() => setStatus(w.id, "rejected")} data-testid={`reject-win-${w.id}`}>
                    Reject
                  </Button>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};
